import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import * as tracking from 'dibs-tracking';
import { Link } from 'dibs-elements/exports/Link';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { NO_CONTENT_LINK } from 'dibs-elements/exports/SeoLink';
import styles from './SbSharedSiblingDesigns.scss';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { FormattedMessage } from 'dibs-react-intl';

const trackSiblingDesignClick = () => {
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'more from iconic design series clicked',
        label: 'design browse page',
    });
};

const trackSeriesParentClick = () => {
    tracking.trackEvent({
        category: 'promo interaction',
        action: 'more from iconic design series clicked',
        label: 'learn more',
    });
};

const SbSharedSiblingDesignsComponent = ({ designPageInfo = {} }) => {
    const siblingDesigns = designPageInfo?.siblingDesigns || [];
    const { displayName: collectionDisplayName, browsePageLinkData: parentLinkData } =
        designPageInfo?.design?.parent || {};
    const { path: seriesParentUrl, isLinkable: seriesParentIsLinkable } = parentLinkData || {};
    const hasLinkableSiblingDesigns = siblingDesigns.some(
        design => design?.browsePageLinkData?.isLinkable
    );

    if (!hasLinkableSiblingDesigns || !collectionDisplayName) {
        return null;
    }

    return (
        <div className={styles.container} data-tn="sibling-designs-container">
            <div className={styles.header} data-tn="sibling-designs-header">
                <div className={styles.title} data-tn="sibling-designs-title">
                    <FormattedMessage
                        id="sb.SbSharedSiblingDesigns.moreFromTheSeries"
                        defaultMessage="The {collectionDisplayName}"
                        values={{ collectionDisplayName }}
                    />
                </div>
                {seriesParentIsLinkable && seriesParentUrl && (
                    <Link
                        className={styles.learnMoreLink}
                        href={seriesParentUrl}
                        onClick={trackSeriesParentClick}
                        dataTn="sibling-designs-learn-more"
                    >
                        <FormattedMessage
                            id="sb.SbSharedSiblingDesigns.learnMoreLink"
                            defaultMessage="Learn More"
                        />
                    </Link>
                )}
            </div>
            <div className={styles.designs} data-tn="sibling-designs">
                {siblingDesigns.map(design => {
                    const {
                        urlLabel,
                        browsePageLinkData = {},
                        browsePageImage,
                        displayName,
                    } = design || {};
                    return (
                        <SeoLink
                            key={`design-${urlLabel}`}
                            linkData={browsePageLinkData}
                            className={styles.design}
                            onClick={trackSiblingDesignClick}
                            dataTn="sibling-design-link"
                            variant={NO_CONTENT_LINK}
                        >
                            <img
                                className={styles.designImage}
                                src={browsePageImage}
                                srcSet={getSrcsetString(browsePageImage)}
                                alt={displayName}
                                data-tn="sibling-design-image"
                            />
                            <div className={styles.designName}>{displayName}</div>
                        </SeoLink>
                    );
                })}
            </div>
        </div>
    );
};

SbSharedSiblingDesignsComponent.propTypes = {
    designPageInfo: PropTypes.object.isRequired,
};

export const SbSharedSiblingDesigns = createFragmentContainer(SbSharedSiblingDesignsComponent, {
    designPageInfo: graphql`
        fragment SbSharedSiblingDesigns_designPageInfo on DesignPageInfo {
            design {
                parent {
                    displayName
                    browsePageLinkData {
                        path
                        isLinkable
                    }
                }
            }
            siblingDesigns {
                displayName
                urlLabel
                browsePageImage
                browsePageLinkData {
                    ...SeoLink_linkData
                    isLinkable
                }
            }
        }
    `,
});
